<script lang="ts">
	import { goto } from '$app/navigation';
	import HeroImageMd from '$lib/assets/page/home/scenic-coastal-city-travel-destinations-brazil-md.avif';
	import HeroImageSm from '$lib/assets/page/home/scenic-coastal-city-travel-destinations-brazil-sm.avif';
	import ImmersionVideoPoster from '$lib/assets/page/home/tokyo-scenic-birdeye-view-sunset-poster.jpg';
	import ImmersionVideo from '$lib/assets/page/home/tokyo-scenic-birdeye-view-sunset.mp4';
	import CityCard from '$lib/components/ui/CityCard.svelte';
	import CollectionCard from '$lib/components/ui/collection/CollectionCard.svelte';
	import HeroImage from '$lib/components/ui/HeroImage.svelte';
	import SmallCard from '$lib/components/ui/SmallCard.svelte';
	import { CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import { FULL_SITE_URL, SITE_NAME } from '$lib/statics';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { formatDownNumber } from '$lib/utils/time.util';
	import { type NavLink, UrlUtil } from '$lib/utils/url.util';
	import { type Component } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconBookOpen from '~icons/mdi/book-open';
	import IconCheckboxMarkedCircle from '~icons/mdi/checkbox-marked-circle';
	import IconDiamond from '~icons/mdi/diamond';
	import IconDice from '~icons/mdi/dice';
	import IconFlame from '~icons/mdi/flame';
	import IconRamen from '~icons/mdi/food-ramen';
	import IconHistory from '~icons/mdi/history';
	import IconArrowDown from '~icons/mdi/keyboard-arrow-down';
	import IconLightbulb from '~icons/mdi/lightbulb';
	import IconMap from '~icons/mdi/map';
	import IconNavigation from '~icons/mdi/navigation';
	import IconPause from '~icons/mdi/pause';
	import IconPlay from '~icons/mdi/play';
	import type { Output } from './+page.server';

	const { data }: { data: { output: Output } } = $props();

	const formattedPlacesCounter = $derived<string>(formatDownNumber(data.output.allPlacesCounter));
	const meta = $derived.by(() => {
		const description = `Explore ${formattedPlacesCounter} places worldwide, plan better trips with city guides, insider tips, hidden gems, must-see attractions, history, maps and day-by-day itineraries.`;
		return {
			title: `${SITE_NAME}: Travel Guides, Things to Do, History & Itineraries`,
			description,
			canonicalUrl: FULL_SITE_URL,
			imageUrl: UrlUtil.getStaticFileFullUrl('images', 'scenic-coastal-city-travel-destinations-brazil-banner.jpg')
		};
	});

	const jsonLd = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: SITE_NAME,
		url: FULL_SITE_URL,
		potentialAction: {
			'@type': 'SearchAction',
			target: `${FULL_SITE_URL}/search?q={search_term_string}`,
			query: 'required'
		}
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />
	<meta name="robots" content="index, follow" />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(jsonLd)}</script>`}
</svelte:head>

<div class="flex min-h-screen flex-col gap-4 overflow-x-hidden">
	<section class="hero min-h-[90vh] overflow-hidden">
		{@render Hero()}
	</section>

	<div class="container mx-auto mb-6 flex flex-col gap-12 px-4 lg:px-0">
		<section>
			{@render FeatureShowcase()}
		</section>
		{@render Values()}
		{@render PlaceCount()}
	</div>

	<section id="featured-cities" class="relative overflow-hidden rounded-none bg-primary/12 py-13 md:rounded-sm">
		{@render FeaturedCities()}
	</section>

	<section id="featured-collections" class="relative overflow-hidden rounded-none bg-secondary/12 py-13 md:rounded-sm">
		{@render Collections()}
	</section>
</div>

{#snippet Hero()}
	<div class="group relative h-full w-full overflow-hidden bg-black">
		<HeroImage
			mdImage={HeroImageMd}
			smImage={HeroImageSm}
			size={{ width: 1900, height: 1267 }}
			altText="Panoramic view of a major coastal city, highlighting urban travel attractions and famous natural landmarks in Rio De Janeiro, Brazil"
		/>

		<div class="relative z-10 container mx-auto flex h-full flex-col px-4 text-center text-white">
			<div class="min-h-20 shrink-0 basis-[15vh]"></div>

			<header class="flex grow flex-col items-center justify-center">
				<h1 class="mb-6 text-4xl leading-10 drop-shadow-lg md:text-8xl md:leading-none">
					Understand Your <br />
					<span class="bg-linear-to-r from-white to-primary bg-clip-text text-transparent"> Next Destination </span>
				</h1>

				<p class="mx-auto mb-10 max-w-2xl text-lg font-medium text-gray-200 md:text-xl">
					Explore {formattedPlacesCounter} places worldwide, from must-see attractions and hidden gems to historic landmarks and local
					favorites. Discover the stories behind the places you visit, with curated itineraries, insider tips and interactive maps.
				</p>

				<div class="flex flex-col items-center justify-center gap-4 md:flex-row">
					{@render ActionButtons()}
				</div>
			</header>

			<div class="flex shrink-0 animate-bounce justify-center py-6 text-base-content/80">
				<IconArrowDown class="h-11 w-11" />
			</div>
		</div>
	</div>

	{#snippet ActionButtons()}
		<a
			class="btn btn-wide rounded-full shadow-none btn-lg btn-primary"
			href="#featured-cities"
			onclick={(e) => {
				e.preventDefault();
				document.getElementById('featured-cities')?.scrollIntoView({ behavior: 'smooth' });
			}}
		>
			Start Exploring
		</a>
		<button
			class="btn rounded-full bg-white/70 text-black/80 btn-lg hover:bg-white/90 hover:text-black/90"
			onclick={() => goto(`/${UrlUtil.toRandomCity().href}`, { state: { isRandomCityNavigation: true } })}
		>
			<IconDice class="text-lg" />
			Random city
		</button>
	{/snippet}
{/snippet}

{#snippet FeatureShowcase()}
	<div class="flex flex-col gap-12">
		<header class="text-center">
			<h2 class="mb-3 text-4xl md:text-5xl">More Than Just A Guidebook</h2>
			<p class="mx-auto max-w-2xl text-xl text-base-content/70">
				We go deeper. Experience cities through rich narratives, curated media, and interactive tools designed for the modern
				traveler.
			</p>
		</header>
		<div class="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
			{@render TourSimulator()}
		</div>
		<div class="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
			{@render History()}
		</div>

		<div class="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
			{@render Multimedia()}
		</div>

		<div class="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
			{@render Insights()}
		</div>
	</div>

	{#snippet TourSimulator()}
		{const nowPassingTitle = 'Colosseum'}
		{const totalDemoDistance = 1850}

		{const demoStops = [
			{ id: 1, pct: 12, name: 'Colosseum' },
			{ id: 2, pct: 32, name: 'Pantheon' },
			{ id: 3, pct: 68, name: 'Roman Forum' },
			{ id: 4, pct: 92, name: 'Spanish Steps' }
		]}

		{const demoWikiPins = [
			{
				title: 'Pantheon',
				imageUrl:
					'https://thumb.wikimedia.org/wikipedia/commons/thumb/0/06/Rome_Pantheon_front.jpg/120px-Rome_Pantheon_front.jpg',
				isPopular: false,
				top: '28%',
				left: '15%'
			},
			{
				title: 'Trevi Fountain',
				imageUrl:
					'https://thumb.wikimedia.org/wikipedia/commons/thumb/b/b1/Rome%2C_Italy%2C_The_Trevi_Fountain_(Fontana_di_Trevi).jpg/120px-Rome%2C_Italy%2C_The_Trevi_Fountain_(Fontana_di_Trevi).jpg',
				isPopular: true,
				top: '78%',
				left: '27%'
			},
			{
				title: 'Appian Way',
				imageUrl: `https://thumb.wikimedia.org/wikipedia/commons/thumb/3/3d/Paesaggio_dell'Appia_antica.jpg/120px-Paesaggio_dell'Appia_antica.jpg`,
				isPopular: true,
				top: '68%',
				left: '78%'
			}
		]}

		{let demoDistance = $state<number>(550)}
		{let isDemoPlaying = $state<boolean>(true)}
		{let activeDemoPinTitle = $state<string | null>('Pantheon')}

		{const currentDemoStop = $derived(
			demoStops.find((s) => Math.round((s.pct / 100) * totalDemoDistance) >= demoDistance) ?? demoStops[demoStops.length - 1]
		)}
		{const distToDemoStop = $derived(Math.max(0, Math.round((currentDemoStop.pct / 100) * totalDemoDistance - demoDistance)))}

		<div class="grid gap-x-12 gap-y-8 lg:grid-cols-2 lg:grid-rows-[auto_auto] lg:content-center">
			<div class="lg:col-start-1 lg:row-start-1 lg:self-end">
				<h3 class="mb-4 text-3xl">Preview routes in 3D before you go</h3>
				<p class="mb-6 text-base text-base-content/70 sm:text-lg">
					Fly along any walking, cycling or self-guided route at street level, right from your browser. The
					<strong class="text-primary">3D tour player</strong> shows you each turn and picks up nearby landmarks, Wikipedia stories
					and photos as you move, so you know what's worth stopping for.
				</p>
				<ul class="flex flex-wrap gap-2" aria-label="Tour simulator features">
					<li><span class="badge badge-soft badge-md badge-accent sm:badge-lg">Street-level 3D</span></li>
					<li><span class="badge badge-soft badge-md badge-accent sm:badge-lg">Nearby landmarks</span></li>
					<li><span class="badge badge-soft badge-md badge-accent sm:badge-lg">Adjustable speed</span></li>
				</ul>
			</div>

			<div class="lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center">
				{@render SimulatorCard()}
			</div>

			<div class="lg:col-start-1 lg:row-start-2 lg:self-start">
				{@render PreviewLink({
					nav: UrlUtil.toCityFeature({
						countrySlug: 'italy',
						citySlug: 'rome',
						type: CityFeatureType.SCENIC_STROLL,
						featureSlug: 'baroque-squares-walk'
					}),
					label: `Preview Rome's Baroque Squares in 3D`,
					btnColourClass: 'btn-accent'
				})}
				<p class="mt-3 text-xs text-base-content/70">Free to use, no sign-up needed</p>
			</div>
		</div>

		{#snippet SimulatorCard()}
			<div
				class="relative w-full overflow-hidden rounded-box border border-base-content/10 bg-base-300/80 p-3.5 shadow-md backdrop-blur-xl sm:p-5"
				role="region"
				aria-label="Interactive Tour Player Demo"
			>
				{@render MapPreview()}
				{@render PlayerHud()}
			</div>
		{/snippet}

		{#snippet MapPreview()}
			<div
				class="relative mb-3 flex h-48 w-full items-center justify-center overflow-hidden rounded-box border border-base-content/5 bg-linear-to-b from-base-100 to-base-200 sm:h-56"
			>
				{@render SonarWave()}

				<div
					class="pointer-events-none relative z-10 flex size-10 items-center justify-center rounded-full bg-primary/20 shadow-md sm:size-12"
				>
					<IconNavigation class="text-xl text-primary" />
				</div>

				{#each demoWikiPins as pin (pin.title)}
					{@render MockWikiMapIcon(pin)}
				{/each}

				{@render NowPassingBadge()}
			</div>

			{#snippet SonarWave()}
				<div
					class={{
						'pointer-events-none absolute size-32 rounded-full border border-primary/40 bg-primary/10 sm:size-44': true,
						'animate-ping': isDemoPlaying
					}}
					style:animation-duration="3s"
				></div>
			{/snippet}
		{/snippet}

		{#snippet NowPassingBadge()}
			<div
				class="pointer-events-none absolute top-2 right-2 z-20 flex max-w-[55%] items-center gap-1.5 rounded-field border border-base-content/10 bg-base-100/95 px-2.5 py-1 shadow-sm sm:top-3 sm:right-3 sm:gap-2 sm:px-3 sm:py-1.5"
			>
				<IconFlame class="shrink-0 text-lg text-highlight" />
				<div class="flex min-w-0 flex-col">
					<span class="font-mono text-xs font-bold text-base-content/70">NOW PASSING</span>
					<span class="truncate text-xs font-semibold text-base-content">
						{nowPassingTitle} · <span class="font-mono text-primary">35m</span>
					</span>
				</div>
			</div>
		{/snippet}

		{#snippet PlayerHud()}
			<div class="flex flex-col gap-2 rounded-box border border-base-200/40 bg-base-100/90 p-2.5 shadow-inner sm:p-3.5">
				{@render Telemetry()}
				{@render Scrubber()}
				{@render Controls()}
			</div>
		{/snippet}

		{#snippet Telemetry()}
			<div class="flex flex-wrap items-center justify-between gap-1.5 border-b border-base-content/10 pb-1.5 font-mono text-xs">
				<div class="flex items-center gap-3">
					<span class="font-bold text-base-content/70">GPS SIM</span>
					<span class="text-base-content/30" aria-hidden="true">·</span>
					<span class="font-semibold text-primary">{Math.round(demoDistance)}m / {totalDemoDistance}m</span>
				</div>

				<div class="flex items-center gap-1.5 text-base-content/80">
					<span class="text-base-content/70">NEXT:</span>
					<span class="font-bold text-base-content">{currentDemoStop.name}</span>
					<span class="badge font-mono badge-xs badge-secondary">{distToDemoStop}m</span>
				</div>
			</div>
		{/snippet}

		{#snippet Scrubber()}
			<div class="relative flex w-full items-center py-2 sm:py-2.5">
				<input
					type="range"
					class="range w-full cursor-pointer range-primary range-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
					min="0"
					max={totalDemoDistance}
					step="10"
					bind:value={demoDistance}
					aria-label="Demo route progress scrubber"
					aria-valuemin="0"
					aria-valuemax={totalDemoDistance}
					aria-valuenow={Math.round(demoDistance)}
					aria-valuetext="{Math.round(demoDistance)} meters of {totalDemoDistance} meters"
				/>

				<div class="pointer-events-none absolute inset-x-2.5 top-1/2 -translate-y-1/2">
					{#each demoStops as stop (stop.id)}
						{const stopDist = $derived<number>(Math.round((stop.pct / 100) * totalDemoDistance))}
						{const isPassed = $derived<boolean>(demoDistance >= stopDist)}

						<button
							type="button"
							class="pointer-events-auto absolute top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-transform hover:scale-125 focus:outline-none"
							style:left="{stop.pct}%"
							title="Jump to Stop {stop.id}: {stop.name}"
							aria-label="Jump to Stop {stop.id}: {stop.name}"
							onclick={() => (demoDistance = stopDist)}
						>
							<span
								class={{
									'flex size-4 items-center justify-center rounded-full font-mono text-[9px] font-bold shadow transition-colors': true,
									'bg-primary text-primary-content ring-2 ring-primary/40': isPassed,
									'bg-base-200 text-base-content/60': !isPassed
								}}
							>
								{stop.id}
							</span>
						</button>
					{/each}
				</div>
			</div>
		{/snippet}

		{#snippet Controls()}
			<div class="flex items-center justify-between pt-0.5">
				<div class="flex items-center gap-1.5 sm:gap-2">
					<button
						type="button"
						class="btn btn-circle shadow-md btn-primary btn-xs"
						aria-label={isDemoPlaying ? 'Pause simulation demo' : 'Resume simulation demo'}
						onclick={() => (isDemoPlaying = !isDemoPlaying)}
					>
						{#if isDemoPlaying}
							<IconPause class="text-xs" />
						{:else}
							<IconPlay class="text-xs" />
						{/if}
					</button>
					<span class="badge badge-ghost font-mono text-xs">2x SPEED</span>
				</div>

				<div class="flex items-center gap-1 font-mono text-xs text-base-content/70">
					<IconHistory class="text-sm text-base-content/60" />
					<span>4 Discovered</span>
				</div>
			</div>
		{/snippet}

		{#snippet MockWikiMapIcon(pin: (typeof demoWikiPins)[number])}
			{const isActive = $derived<boolean>(activeDemoPinTitle === pin.title)}

			<button
				class={{
					'group pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-transform duration-200 select-none': true,
					'z-30': isActive,
					'z-10 hover:scale-105': !isActive
				}}
				type="button"
				style:top={pin.top}
				style:left={pin.left}
				onclick={() => (activeDemoPinTitle = isActive ? null : pin.title)}
				aria-label="Demo landmark: {pin.title}"
			>
				<div class="flex flex-col items-center">
					<div
						class={{
							'pointer-events-none relative z-20 inline-flex items-center rounded-field px-1.5 py-0.5 text-xs whitespace-nowrap transition-all duration-200': true,
							'-translate-y-3 scale-110': isActive,
							'border border-highlight bg-highlight-bg/90 text-highlight-content': pin.isPopular,
							'border border-white/20 bg-base-300/90 text-base-content': !pin.isPopular
						}}
					>
						{#if pin.isPopular}
							<IconFlame class="mr-1 text-xs text-highlight sm:text-xs" />
						{/if}
						<span class="truncate">{pin.title}</span>
					</div>

					<div
						class={{
							'relative flex size-10 items-center justify-center overflow-hidden rounded-full bg-accent text-accent-content shadow-md transition-all duration-200 sm:size-12': true,
							'z-10 scale-135 border-2 border-primary': isActive,
							'border-2 border-highlight ring-2 ring-highlight/50': !isActive && pin.isPopular,
							'border-2 border-accent-content': !isActive && !pin.isPopular
						}}
					>
						<img class="size-full object-cover" src={pin.imageUrl} alt={pin.title} decoding="async" draggable="false" />
					</div>
				</div>
			</button>
		{/snippet}
	{/snippet}

	{#snippet History()}
		<div class="grid gap-x-12 gap-y-8 lg:grid-cols-2 lg:grid-rows-[auto_auto] lg:content-center">
			<div class="lg:col-start-2 lg:row-start-1 lg:self-end">
				<h3 class="mb-4 text-3xl">Uncover the History Behind Every Place</h3>
				<p class="mb-6 text-lg text-base-content/70">
					Every place has a past. Our guides feature <strong class="text-primary">Historical Timelines</strong>
					and deep-dive histories that give context to the sights you're seeing.
				</p>
				<div class="flex gap-4">
					<div class="badge badge-soft badge-lg badge-primary">Story</div>
					<div class="badge badge-soft badge-lg badge-primary">Culture</div>
				</div>
			</div>

			<div class="lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:self-center">
				<div class="relative rounded-box border border-base-content/5 bg-base-200 p-8 shadow-md">
					{@render Timeline()}
				</div>
			</div>

			<div class="lg:col-start-2 lg:row-start-2 lg:self-start">
				{@render PreviewLink({
					nav: UrlUtil.toCity({ countrySlug: 'france', citySlug: 'paris', section: 'history' }),
					label: `Explore Paris's history`,
					btnColourClass: 'btn-primary'
				})}
			</div>
		</div>

		{#snippet Timeline()}
			<div class="absolute -top-6 -left-6 z-20 rounded-box bg-primary p-4 text-primary-content shadow-md">
				<IconBookOpen class="text-lg md:text-2xl" />
			</div>
			<ul class="timeline timeline-vertical timeline-snap-icon max-md:timeline-compact">
				<li>
					<div class="timeline-middle"><IconCheckboxMarkedCircle class="text-primary" /></div>
					<div class="timeline-end mb-10">
						<time class="font-mono italic">1889</time>
						<div class="text-lg font-black">Eiffel Tower Opens</div>
						Originally criticized, now an icon.
					</div>
					<hr class="bg-primary" />
				</li>
				<li>
					<hr class="bg-primary" />
					<div class="timeline-middle"><IconCheckboxMarkedCircle class="text-primary" /></div>
					<div class="timeline-end mb-10">
						<time class="font-mono italic">1920</time>
						<div class="text-lg font-black">The Jazz Age</div>
						Paris becomes the hub of art.
					</div>
				</li>
			</ul>
		{/snippet}
	{/snippet}

	{#snippet Multimedia()}
		<div class="grid gap-x-12 gap-y-8 lg:grid-cols-2 lg:grid-rows-[auto_auto] lg:content-center">
			<div class="lg:col-start-1 lg:row-start-1 lg:self-end">
				<h3 class="mb-4 text-3xl">Immersive Map Exploration</h3>
				<p class="mb-6 text-lg text-base-content/70">
					Get a feel for the vibe before you book. We pair high-quality <strong class="text-primary">Video Tours</strong> with our
					signature <strong class="text-primary">Wikipedia Map Explorer</strong>. Move the map to automatically populate local
					points of interest, and click on any landmark to instantly reveal its history, summaries, and photo galleries in
					real-time.
				</p>
				<div class="flex gap-4">
					<div class="badge badge-soft badge-lg badge-secondary">4K Video</div>
					<div class="badge badge-soft badge-lg badge-secondary">Wikipedia Map Explorer</div>
				</div>
			</div>

			<div class="lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center">
				<div class="group relative overflow-hidden rounded-3xl shadow-2xl transition-transform duration-500 hover:rotate-1">
					{@render VideoCard()}
				</div>
			</div>

			<div class="lg:col-start-1 lg:row-start-2 lg:self-start">
				{@render PreviewLink({
					nav: UrlUtil.toCity({ countrySlug: 'japan', citySlug: 'tokyo', section: 'map' }),
					label: `Explore Tokyo on the map`,
					btnColourClass: 'btn-secondary'
				})}
			</div>
		</div>

		{#snippet VideoCard()}
			<div class="relative h-72 w-full overflow-hidden rounded-box bg-black">
				<video
					class="h-full w-full object-cover opacity-80"
					src={ImmersionVideo}
					poster={ImmersionVideoPoster}
					width="1280"
					height="720"
					preload="none"
					autoplay
					loop
					muted
					playsinline
					disablepictureinpicture
					aria-label="Scenic view of Tokyo from a birdeye view during sunset"
				></video>
				<div class="absolute bottom-4 left-4 text-lg font-bold text-white drop-shadow-md">Walking the Streets of Tokyo</div>
			</div>
		{/snippet}
	{/snippet}

	{#snippet Insights()}
		<div class="order-2 lg:order-1 lg:w-1/2">
			{@render FactCard()}
		</div>
		<div class="order-1 lg:order-2 lg:w-1/2">
			<h3 class="mb-4 text-3xl">Smarter Travel Tips</h3>
			<p class="mb-6 text-lg text-base-content/70">
				Skip the generic advice. Our <strong class="text-primary">Insider Tips</strong> and
				<strong class="text-primary">Fun Facts</strong> help you travel like a pro, avoid tourist traps, and impress your friends with
				local knowledge.
			</p>
		</div>

		{#snippet FactCard()}
			<div class="card border-l-8 border-info bg-base-200/65 text-base-content shadow-md">
				<div class="card-body">
					<div class="flex flex-row items-center gap-2">
						<IconLightbulb class="text-lg" />
						<h2 class="card-title">Did You Know?</h2>
					</div>

					<p class="text-base-content/70">
						Venice sits on millions of petrified wooden piles driven into the marshy ground. They are still intact today!
					</p>
					<div class="mt-4 card-actions justify-end">
						<div class="badge badge-soft badge-lg badge-info">Architecture</div>
						<div class="badge badge-soft badge-lg badge-info">Secret</div>
					</div>
				</div>
			</div>
		{/snippet}
	{/snippet}

	{#snippet PreviewLink({ nav, label, btnColourClass }: { nav: NavLink; label: string; btnColourClass: ClassValue })}
		<a
			class="group btn w-full rounded-full font-mono shadow-md btn-sm md:btn-md {btnColourClass}"
			href="/{nav.href}"
			data-sveltekit-preload-data={nav.preloadData}
			data-sveltekit-preload-code={nav.preloadCode}
		>
			<span>{label}</span>
			<span class="transition-transform duration-200 group-hover:translate-x-1">
				<IconArrowRight class="size-4" />
			</span>
		</a>
	{/snippet}
{/snippet}

{#snippet Values()}
	{const values: { title: string; desc: string; Icon: Component }[] = [
		{ title: 'Curated Itineraries', desc: 'Day-by-day plans filled with must-see places', Icon: IconMap },
		{ title: 'Culinary Secrets', desc: 'From street food markets to Michelin-star dining', Icon: IconRamen },
		{ title: 'Hidden Gems', desc: 'Discover the spots that guidebooks miss', Icon: IconDiamond }
	]}
	<div class="grid grid-cols-1 gap-4 text-center md:grid-cols-3">
		{#each values as feature (feature.title)}
			<SmallCard title={feature.title} desc={feature.desc} Icon={feature.Icon} />
		{/each}
	</div>
{/snippet}

{#snippet PlaceCount()}
	<p class="text-center font-mono text-lg tracking-[0.25em] text-base-content/75">
		{formattedPlacesCounter} PLACES TO EXPLORE
	</p>
{/snippet}

{#snippet FeaturedCities()}
	{const nav = UrlUtil.toCities()}

	<div class="relative z-10 container mx-auto px-4">
		<div class="mx-auto mb-12 max-w-xl text-center">
			<p class="mb-4 badge badge-soft font-mono text-xs tracking-[0.25em] badge-primary">EXPLORE BY CITY</p>
			<h2 class="mb-5 text-4xl font-medium tracking-tight md:text-5xl">Where will you go next?</h2>
			<p class="text-lg text-base-content/70">
				From world capitals to hidden gems, every city here has its own story waiting to be uncovered.
			</p>
			<div class="mx-auto mt-6 h-px w-16 bg-primary/70"></div>
		</div>

		<ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{#each data.output.featuredCities as city (city.id)}
				<li>
					<CityCard {city} />
				</li>
			{/each}
		</ul>

		<div class="mt-14 flex justify-center">
			<a
				class="group btn w-full rounded-full btn-primary lg:btn-wide"
				href="/{nav.href}"
				data-sveltekit-preload-data={nav.preloadData}
				data-sveltekit-preload-code={nav.preloadCode}
			>
				View all cities
				<span class="transition-transform group-hover:translate-x-1">
					<IconArrowRight class="size-4" />
				</span>
			</a>
		</div>
	</div>
{/snippet}

{#snippet Collections()}
	{const nav = UrlUtil.toAll()}

	<div class="relative z-10 container mx-auto px-4">
		<div class="mx-auto mb-12 max-w-xl text-center">
			<p class="mb-4 badge badge-soft font-mono text-xs tracking-[0.25em] badge-secondary">EXPLORE BY THEME</p>
			<h2 class="mb-5 text-4xl font-medium tracking-tight md:text-5xl">Pick what draws you in.</h2>
			<p class="text-lg text-base-content/70">
				Castles, sacred spaces, hidden coastlines, and more. Skip the single-city search and browse the world by theme instead.
			</p>
			<div class="mx-auto mt-6 h-px w-16 bg-secondary/70"></div>
		</div>

		<ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{#each data.output.collections as collection, i (collection.metadata.slug)}
				<li>
					<CollectionCard
						{collection}
						nav={UrlUtil.toAllCollection({ slug: collection.metadata.slug })}
						index={i}
						loading={'lazy'}
					/>
				</li>
			{/each}
		</ul>

		<div class="mt-14 flex justify-center">
			<a
				class="group btn w-full rounded-full btn-secondary lg:btn-wide"
				href="/{nav.href}"
				data-sveltekit-preload-data={nav.preloadData}
				data-sveltekit-preload-code={nav.preloadCode}
			>
				View all collections
				<span class="transition-transform group-hover:translate-x-1">
					<IconArrowRight class="size-4" />
				</span>
			</a>
		</div>
	</div>
{/snippet}
